/**
 * Games (Phase 6 / Module 2) validation contracts.
 *
 * Module 2 is a thin orchestration layer above the Phase 4 Assessment
 * Engine. The only new persistent state is the `game_shares` ledger; the
 * "what's playable now" and "my stats" views are derived from the engine
 * tables. These schemas describe:
 *
 *   1. POST /api/games/shares request body — what the post-quiz popup
 *      sends when a student taps WhatsApp / native share / copy link.
 *      Server re-derives `quiz_scope` and `score_percent` from the parent
 *      attempt; the client cannot lie about those.
 *   2. POST /api/games/shares response — the recorded row id, plus a
 *      `dedupedFromPriorEvent` boolean so the client knows the row was
 *      collapsed against a prior matching `clientEventId`.
 *   3. GET /api/me/games response — list of playable runs for the calling
 *      student, with a fresh "attempts remaining" count and a latest-
 *      attempt summary.
 *   4. GET /api/me/games/stats response — three small numbers powering
 *      the dashboard card (current weekly streak, this-month average
 *      score, lifetime quiz count).
 *
 * Spec: docs/deep-research-report4.md §"Student & Teacher Game System".
 * Roadmap: docs/implementation-plan-v2.md §9.6.
 */
import { z } from 'zod';
import { idSchema } from './common.js';

// ----- Channel enum -------------------------------------------------------

/**
 * The four share channels the popup surfaces. Stored as plain text in the
 * `game_shares` table so new channels can ship without a migration; the
 * Zod enum is the application-layer trust boundary.
 */
export const gameShareChannelSchema = z.enum([
  'whatsapp_chat',
  'whatsapp_status',
  'other_link_copy',
  'other_native_share',
]);
export type GameShareChannel = z.infer<typeof gameShareChannelSchema>;

/**
 * Quiz scopes Module 2 exposes today. The Assessment Engine has a wider
 * scope enum (`hiring`, `rules`, `weekly_quiz`, `monthly_quiz`,
 * `interview_script`) — only the two quiz scopes appear in the Games
 * surface, derived server-side from the attempt's parent template.
 */
export const gameQuizScopeSchema = z.enum(['weekly_quiz', 'monthly_quiz']);
export type GameQuizScope = z.infer<typeof gameQuizScopeSchema>;

// ----- POST /api/games/shares request ------------------------------------

/**
 * Body for POST /api/games/shares.
 *
 * Trust boundary notes — only `attemptId`, `channel`, `shareUrl` and the
 * optional `clientEventId` are taken from the client. Any `quizScope` or
 * `scorePercent` fields supplied by the client are IGNORED at the route
 * layer (the route re-derives them from the attempt). They are NOT part
 * of this schema so a buggy or malicious client cannot smuggle them in
 * via `.passthrough()` semantics — the `.strict()` rejects unknown keys
 * outright.
 *
 * The shareUrl is validated for length and URL shape here; the
 * babulilmonline.com prefix check is enforced at the route layer (it
 * depends on environment configuration, not a constant).
 */
export const gameShareCreateSchema = z
  .object({
    attemptId: idSchema,
    channel: gameShareChannelSchema,
    shareUrl: z
      .string()
      .trim()
      .min(1, 'shareUrl is required')
      .max(2048, 'shareUrl is too long')
      .url('shareUrl must be a valid URL'),
    /**
     * Optional client-minted idempotency token. When supplied, the server
     * uses `(attempt_id, channel, client_event_id)` as a dedupe key so a
     * rapid double-tap on the share button does not double-count.
     */
    clientEventId: z.string().trim().min(1).max(128).optional(),
  })
  .strict();
export type GameShareCreateInput = z.infer<typeof gameShareCreateSchema>;

// ----- POST /api/games/shares response -----------------------------------

export const gameShareCreateResponseSchema = z.object({
  id: z.string(),
  /** True when the row was collapsed against a prior matching clientEventId. */
  dedupedFromPriorEvent: z.boolean(),
});
export type GameShareCreateResponse = z.infer<typeof gameShareCreateResponseSchema>;

/**
 * Alias used by the spec under the name `gameSharesResponseSchema`. Same
 * shape — keeps both naming conventions valid for downstream consumers.
 */
export const gameSharesResponseSchema = gameShareCreateResponseSchema;

// ----- GET /api/me/games response ----------------------------------------

/**
 * Per-run latest-attempt summary surfaced inside `MyGamesItem`. Null when
 * the student has never started this run.
 */
export const myGamesLatestAttemptSchema = z.object({
  id: z.string(),
  status: z.enum(['in_progress', 'submitted', 'expired', 'void']),
  scorePercent: z.number().int().nullable(),
});
export type MyGamesLatestAttempt = z.infer<typeof myGamesLatestAttemptSchema>;

export const myGamesItemSchema = z.object({
  runId: z.string(),
  templateId: z.string(),
  title: z.string(),
  scope: gameQuizScopeSchema,
  /**
   * Subject label rendered on the card. Resolved server-side: course name
   * when the template's owner is a course; otherwise the template title.
   */
  subject: z.string(),
  periodKey: z.string().nullable(),
  opensAt: z.string().nullable(),
  closesAt: z.string().nullable(),
  questionCount: z.number().int().min(0),
  attemptsRemaining: z.number().int().min(0),
  latestAttempt: myGamesLatestAttemptSchema.nullable(),
});
export type MyGamesItem = z.infer<typeof myGamesItemSchema>;

export const myGamesResponseSchema = z.object({
  items: z.array(myGamesItemSchema),
});
export type MyGamesResponse = z.infer<typeof myGamesResponseSchema>;

// ----- GET /api/me/games/stats response ----------------------------------

export const myGamesStatsResponseSchema = z.object({
  /**
   * Number of consecutive ISO weeks (ending at the current one) where the
   * student completed at least one weekly_quiz attempt with `pass=true`.
   * 0 when no streak is active.
   */
  currentStreakWeeks: z.number().int().min(0),
  /**
   * Average `score_percent` across all submitted/expired attempts in the
   * current calendar month, rounded to the nearest integer. Null when no
   * attempts have been submitted this month.
   */
  averageScorePercentThisMonth: z.number().int().min(0).max(100).nullable(),
  /**
   * Lifetime count of submitted/expired weekly_quiz + monthly_quiz
   * attempts. Excludes void attempts.
   */
  totalQuizzesPlayed: z.number().int().min(0),
});
export type MyGamesStatsResponse = z.infer<typeof myGamesStatsResponseSchema>;
